/**
 * Today, for a screen that stays open.
 *
 * `hoy()` is read when a screen draws, and a screen left open overnight drew yesterday all the next
 * morning: the home screen's «Vendido hoy» summed the day before, «Empezar el día» was «Empezar mi
 * turno», «Contabilidad» offered last month as this one (2026-10-13). This draws it again when the
 * day turns at midnight in Nicaragua, and at four in the morning, when the shop's day does
 * (`hoyDelNegocio`).
 */

import { useEffect, useState } from 'react';
import { ZONA_HORARIA, hoy } from '@abastece/shared';

const reloj = new Intl.DateTimeFormat('en-US', {
  timeZone: ZONA_HORARIA,
  hourCycle: 'h23',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

const HORA_MS = 3_600_000;

/** How long until the next midnight or four in the morning in Nicaragua, a second past it. */
export function msHastaQueCambieElDia(ahora: Date): number {
  const p = reloj.formatToParts(ahora);
  const v = (tipo: Intl.DateTimeFormatPartTypes) => Number(p.find((x) => x.type === tipo)!.value);
  const transcurrido = ((v('hour') * 60 + v('minute')) * 60 + v('second')) * 1000 + ahora.getMilliseconds();
  const siguiente = transcurrido < 4 * HORA_MS ? 4 * HORA_MS : 24 * HORA_MS;
  return siguiente - transcurrido + 1000;
}

/** Today at the shop, drawn again when it turns (`msHastaQueCambieElDia`). */
export function useHoy(): string {
  const [, setVuelta] = useState(0);
  useEffect(() => {
    const id = setTimeout(() => setVuelta((n) => n + 1), msHastaQueCambieElDia(new Date()));
    return () => clearTimeout(id);
  });
  return hoy();
}
